<template>
  <div
    v-if="isAgentless"
    :class="$style.container"
  >
    <ul :class="$style.list">
      <li
        v-if="showSecureLoginEnabled"
        :class="$style.statusItem"
        :data-test-id="$testId('secureLoginEnabled')"
      >
        <Status v-bind="secureLoginEnabledProps" />
      </li>
      <li
        v-if="showSystemInsights"
        :class="$style.statusItem"
      >
        <Status v-bind="systemInsightsProps" />
      </li>
      <li
        :class="$style.statusItem"
      >
        <Status v-bind="mfaStatusProps" />
      </li>
    </ul>
    <SystemEnableMFAModal
      :item="item"
      :performMFAAction="false"
      :visible="showDeviceEnableMfaModal"
      @updateVisibility="handleUpdateMfaModalVisibility"
    />
  </div>
</template>

<script>
import {
  ItemTypeConsts, StatusColorNames, StoreModelMap, StoreModuleMap,
} from 'client/util/Constants';
import { mapActions, mapGetters, mapState } from 'vuex';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import Status from 'client/components/Status/Status.vue';
import SystemEnableMFAModal from 'client/components/Modal/mfaModals/SystemEnableMFAModal.vue';
import SystemInsightsConstants from '../util/Constants/SystemInsightsConstants';

const systemModel = StoreModelMap[ItemTypeConsts.system];
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];
const { enabled, deferred } = SystemInsightsConstants.systemState;

export default {
  name: 'DeviceStatus',

  components: {
    Status,
    SystemEnableMFAModal,
  },

  data: () => ({
    showDeviceEnableMfaModal: false,
  }),

  computed: {
    ...mapState(systemModel, ['item']),

    ...mapGetters(systemModel, {
      isAndroid: 'isAndroid',
      isIOS: 'isIOS',
      isLinux: 'isLinux',
      isSecureLoginEnabled: 'isSecureLoginEnabled',
      isSystemInsightsSupportedBySystem: 'isSystemInsightsSupported',
    }),

    ...mapGetters(settingsModule, ['hasSystemInsights']),

    isAgentless() {
      return !this.isIOS && !this.isAndroid;
    },

    isMfaDisabled() {
      return this.isLinux && this.item.allowPublicKeyAuthentication
             && this.item.allowSshPasswordAuthentication;
    },

    mfaMenuItemLabel() {
      return this.item.allowMultiFactorAuthentication
        ? 'Disable MFA Login'
        : 'Enable MFA Login';
    },

    mfaStatusColor() {
      return this.item.allowMultiFactorAuthentication
        ? StatusColorNames.active
        : StatusColorNames.inactive;
    },

    mfaStatusLabel() {
      return this.item.allowMultiFactorAuthentication
        ? 'MFA Login Enabled'
        : 'MFA Login Disabled';
    },

    mfaStatusProps() {
      return [{
        label: this.mfaStatusLabel,
        color: this.mfaStatusColor,
        disabled: this.isMfaDisabled,
        subtext: this.isMfaDisabled ? 'MFA cannot be enabled if both Allow SSH Password Login AND Enable Public Key Authentication in SSH Settings are selected. Please deselect one or both to continue.' : '',
        menuSections: [
          {
            menuItems: [
              {
                label: this.mfaMenuItemLabel,
                action: this.handleMfaChange,
              },
            ],
          },
        ],
      }];
    },

    secureLoginEnabledColor() {
      return this.isSecureLoginEnabled
        ? StatusColorNames.active
        : StatusColorNames.inactive;
    },

    secureLoginEnabledLabel() {
      return this.isSecureLoginEnabled
        ? 'Secure Login Enabled'
        : 'Secure Login Disabled';
    },

    secureLoginEnabledProps() {
      return [{
        label: this.secureLoginEnabledLabel,
        color: this.secureLoginEnabledColor,
      }];
    },

    showSecureLoginEnabled() {
      return this.$hasFeatureFlag(this.$featureFlags.deviceAsAGateway);
    },

    showSystemInsights() {
      return this.isSystemInsightsSupportedBySystem && this.hasSystemInsights;
    },

    systemInsightsMenuItemLabel() {
      return this.item.systemInsights.state === enabled
        ? 'Disable System Insights'
        : 'Enable System Insights';
    },

    systemInsightsProps() {
      return [{
        label: this.systemInsightsStatusLabel,
        color: this.systemInsightsStatusColor,
        menuSections: [
          {
            menuItems: [
              {
                label: this.systemInsightsMenuItemLabel,
                action: this.handleSystemInsightsChange,
              },
            ],
          },
        ],
      }];
    },

    systemInsightsStatusColor() {
      return this.item.systemInsights.state === enabled
        ? StatusColorNames.active
        : StatusColorNames.inactive;
    },

    systemInsightsStatusLabel() {
      return this.item.systemInsights.state === enabled
        ? 'System Insights Enabled'
        : 'System Insights Disabled';
    },
  },

  methods: {
    ...mapActions(systemModel, ['setItemField']),

    handleMfaChange() {
      const value = !this.item.allowMultiFactorAuthentication;
      if (value) this.showDeviceEnableMfaModal = true;
      const key = 'allowMultiFactorAuthentication';
      this.setItemField({ key, value });
    },

    handleSystemInsightsChange() {
      const state = this.item.systemInsights.state === enabled ? deferred : enabled;
      if (state === enabled) this.openDeviceSIModal();
      const key = 'systemInsights';
      const value = { state };
      this.setItemField({ key, value });
    },

    handleUpdateMfaModalVisibility(visible) {
      this.showDeviceEnableMfaModal = visible;
    },

    openDeviceSIModal() {
      ModalEventBus.open({
        component: 'DeviceEnableSystemInsightsModal',
      });
    },
  },
};
</script>
<style module>

.container {
  min-width: 160px;
}

.list {
  display: inline-block;
  text-align: left;
}

.statusItem {
  list-style-type: none;
  padding: 6px 0;
}

</style>
